import { apiCallPost } from '../fns/util';

/**
 * AuditService - Centralized service for audit logging
 * Tracks who did what at what time for every database change
 */
class AuditService {
  
  /**
   * Log an audit event
   * @param {Object} params - Audit parameters
   * @param {string} params.entityType - Type of entity being modified (e.g., 'insurancePanel', 'branch', 'patient')
   * @param {string} params.entityId - ID of the entity being modified
   * @param {string} params.action - Action performed (CREATE, UPDATE, DELETE, VIEW)
   * @param {Object} params.changes - Object containing what changed (before/after values)
   * @param {string} params.userId - ID of the user performing the action
   * @param {string} params.userName - Name of the user performing the action
   * @param {string} params.userEmail - Email of the user performing the action
   * @param {string} params.organizationId - Organization/hospital ID (prefix)
   * @param {string} params.ipAddress - IP address of the user
   * @param {string} params.userAgent - Browser user agent
   * @param {string} params.sessionId - Session ID for tracking
   * @param {Object} params.metadata - Additional metadata (browser info, location, etc.)
   * @param {string} params.description - Human-readable description of the action
   */
  static async logAuditEvent({
    entityType,
    entityId,
    action,
    changes = {},
    userId,
    userName,
    userEmail,
    organizationId,
    ipAddress,
    userAgent,
    sessionId,
    metadata = {},
    description
  }) {
    console.log('🔍 AUDIT SERVICE: Starting audit log creation', {
      entityType,
      entityId,
      action,
      userId,
      userName,
      organizationId,
      description
    });
    
    try {
      const auditLog = {
        _id: `audit_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        timestamp: new Date().toISOString(),
        entityType,
        entityId,
        action,
        changes,
        user: {
          id: userId,
          name: userName,
          email: userEmail
        },
        organization: {
          id: organizationId
        },
        technical: {
          ipAddress,
          userAgent,
          sessionId,
          ...metadata
        },
        description
      };

      console.log('🔍 AUDIT SERVICE: Audit log object created', auditLog);

      // Store in sessionStorage for local session-based audit
      this.storeLocalAuditLog(auditLog);
      
      console.log('✅ AUDIT SERVICE: Audit log stored locally:', auditLog._id);
      return auditLog;
    } catch (error) {
      console.error('❌ AUDIT SERVICE: Failed to create audit log:', error);
      console.error('❌ AUDIT SERVICE: Error details:', {
        message: error.message,
        stack: error.stack,
        organizationId,
        entityType,
        entityId
      });
      // Don't throw error to prevent breaking the main operation
      return null;
    }
  }

  /**
   * Store audit log in sessionStorage
   * @param {Object} auditLog - Audit log object to store
   */
  static storeLocalAuditLog(auditLog) {
    try {
      const existingLogs = this.getLocalAuditLogs();
      const updatedLogs = [auditLog, ...existingLogs]; // Add new log at the beginning
      
      // Keep only last 100 logs to prevent storage bloat
      const trimmedLogs = updatedLogs.slice(0, 100);
      
      sessionStorage.setItem('localAuditLogs', JSON.stringify(trimmedLogs));
      console.log('🔍 AUDIT SERVICE: Stored audit log locally', auditLog._id);
    } catch (error) {
      console.error('❌ AUDIT SERVICE: Failed to store audit log locally:', error);
    }
  }

  /**
   * Get audit logs from sessionStorage
   * @returns {Array} Array of audit logs
   */
  static getLocalAuditLogs() {
    try {
      const logs = sessionStorage.getItem('localAuditLogs');
      return logs ? JSON.parse(logs) : [];
    } catch (error) {
      console.error('❌ AUDIT SERVICE: Failed to get local audit logs:', error);
      return [];
    }
  }

  /**
   * Get audit logs for a specific entity
   * @param {string} organizationId - Organization ID
   * @param {string} entityType - Type of entity
   * @param {string} entityId - ID of the entity
   * @param {Object} options - Query options (limit, offset, dateRange, etc.)
   */
  static async getAuditLogs(organizationId, entityType, entityId, options = {}) {
    console.log('🔍 AUDIT SERVICE: Getting audit logs', {
      organizationId,
      entityType,
      entityId,
      options
    });
    
    try {
      // Get logs from local storage
      const allLogs = this.getLocalAuditLogs();
      console.log('🔍 AUDIT SERVICE: Retrieved local logs', allLogs.length);
      
      // Filter logs based on parameters
      let filteredLogs = allLogs;
      
      if (entityType) {
        filteredLogs = filteredLogs.filter(log => log.entityType === entityType);
      }
      
      if (entityId) {
        filteredLogs = filteredLogs.filter(log => log.entityId === entityId);
      }
      
      if (options.searchText) {
        const searchText = options.searchText.toLowerCase();
        filteredLogs = filteredLogs.filter(log => 
          log.description.toLowerCase().includes(searchText) ||
          log.entityType.toLowerCase().includes(searchText) ||
          log.entityId.toLowerCase().includes(searchText)
        );
      }
      
      // Apply pagination
      const limit = options.limit || 20;
      const offset = options.offset || 0;
      const paginatedLogs = filteredLogs.slice(offset, offset + limit);
      
      const response = {
        logs: paginatedLogs,
        total: filteredLogs.length,
        hasMore: (offset + limit) < filteredLogs.length
      };
      
      console.log('✅ AUDIT SERVICE: Returning filtered logs', {
        total: response.total,
        returned: response.logs.length,
        hasMore: response.hasMore
      });
      
      return response;
    } catch (error) {
      console.error('❌ AUDIT SERVICE: Failed to get audit logs:', error);
      return {
        logs: [],
        total: 0,
        hasMore: false
      };
    }
  }

  /**
   * Get audit logs for a specific user
   * @param {string} organizationId - Organization ID
   * @param {string} userId - User ID
   * @param {Object} options - Query options
   */
  static async getUserAuditLogs(organizationId, userId, options = {}) {
    try {
      const response = await apiCallPost(
        `/audit/${organizationId}/getUserLogs`,
        {
          userId,
          ...options
        }
      );
      return response;
    } catch (error) {
      console.error('Failed to fetch user audit logs:', error);
      return [];
    }
  }

  /**
   * Get audit logs for a specific action type
   * @param {string} organizationId - Organization ID
   * @param {string} action - Action type (CREATE, UPDATE, DELETE, VIEW)
   * @param {Object} options - Query options
   */
  static async getActionAuditLogs(organizationId, action, options = {}) {
    try {
      const response = await apiCallPost(
        `/audit/${organizationId}/getActionLogs`,
        {
          action,
          ...options
        }
      );
      return response;
    } catch (error) {
      console.error('Failed to fetch action audit logs:', error);
      return [];
    }
  }

  /**
   * Get comprehensive audit report
   * @param {string} organizationId - Organization ID
   * @param {Object} filters - Filters for the report
   */
  static async getAuditReport(organizationId, filters = {}) {
    try {
      const response = await apiCallPost(
        `/audit/${organizationId}/getReport`,
        filters
      );
      return response;
    } catch (error) {
      console.error('Failed to fetch audit report:', error);
      return null;
    }
  }

  /**
   * Helper method to extract user information from authStore
   * @param {Object} authStore - MobX auth store
   */
  static extractUserInfo(authStore) {
    return {
      userId: authStore.id || 'unknown',
      userName: authStore.name || 'Unknown User',
      userEmail: authStore.email || 'unknown@example.com',
      organizationId: authStore.prefix || 'unknown'
    };
  }

  /**
   * Helper method to get browser/technical information
   */
  static getTechnicalInfo() {
    return {
      ipAddress: 'client-side', // Will be determined server-side
      userAgent: navigator.userAgent,
      sessionId: this.getSessionId(),
      browserInfo: {
        language: navigator.language,
        platform: navigator.platform,
        cookieEnabled: navigator.cookieEnabled,
        onLine: navigator.onLine
      },
      screenInfo: {
        width: window.screen.width,
        height: window.screen.height,
        colorDepth: window.screen.colorDepth
      },
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    };
  }

  /**
   * Get or create session ID
   */
  static getSessionId() {
    let sessionId = sessionStorage.getItem('auditSessionId');
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('auditSessionId', sessionId);
    }
    return sessionId;
  }

  /**
   * Helper method to create audit log for entity updates
   * @param {Object} params - Parameters for the audit log
   */
  static async logEntityUpdate({
    entityType,
    entityId,
    oldData,
    newData,
    authStore,
    description
  }) {
    const userInfo = this.extractUserInfo(authStore);
    const technicalInfo = this.getTechnicalInfo();

    // Calculate changes
    const changes = this.calculateChanges(oldData, newData);

    return await this.logAuditEvent({
      entityType,
      entityId,
      action: 'UPDATE',
      changes,
      ...userInfo,
      ...technicalInfo,
      description: description || `Updated ${entityType} ${entityId}`
    });
  }

  /**
   * Helper method to create audit log for entity creation
   * @param {Object} params - Parameters for the audit log
   */
  static async logEntityCreate({
    entityType,
    entityId,
    newData,
    authStore,
    description
  }) {
    console.log('🔍 AUDIT SERVICE: logEntityCreate called', {
      entityType,
      entityId,
      authStore: authStore ? 'present' : 'missing',
      description
    });
    
    const userInfo = this.extractUserInfo(authStore);
    const technicalInfo = this.getTechnicalInfo();
    
    console.log('🔍 AUDIT SERVICE: Extracted user info', userInfo);
    console.log('🔍 AUDIT SERVICE: Extracted technical info', technicalInfo);

    return await this.logAuditEvent({
      entityType,
      entityId,
      action: 'CREATE',
      changes: { created: newData },
      ...userInfo,
      ...technicalInfo,
      description: description || `Created ${entityType} ${entityId}`
    });
  }

  /**
   * Helper method to create audit log for entity deletion
   * @param {Object} params - Parameters for the audit log
   */
  static async logEntityDelete({
    entityType,
    entityId,
    deletedData,
    authStore,
    description
  }) {
    const userInfo = this.extractUserInfo(authStore);
    const technicalInfo = this.getTechnicalInfo();

    return await this.logAuditEvent({
      entityType,
      entityId,
      action: 'DELETE',
      changes: { deleted: deletedData },
      ...userInfo,
      ...technicalInfo,
      description: description || `Deleted ${entityType} ${entityId}`
    });
  }

  /**
   * Calculate changes between old and new data
   * @param {Object} oldData - Previous data
   * @param {Object} newData - New data
   */
  static calculateChanges(oldData, newData) {
    const changes = {};
    
    // Compare all keys in both objects
    const allKeys = new Set([...Object.keys(oldData || {}), ...Object.keys(newData || {})]);
    
    for (const key of allKeys) {
      const oldValue = oldData?.[key];
      const newValue = newData?.[key];
      
      if (!this.isEqual(oldValue, newValue)) {
        changes[key] = {
          from: oldValue,
          to: newValue
        };
      }
    }
    
    return changes;
  }

  /**
   * Deep equality check for objects
   * @param {*} a - First value
   * @param {*} b - Second value
   */
  static isEqual(a, b) {
    if (a === b) return true;
    if (a == null || b == null) return false;
    if (typeof a !== typeof b) return false;
    
    if (typeof a === 'object') {
      if (Array.isArray(a) !== Array.isArray(b)) return false;
      
      if (Array.isArray(a)) {
        if (a.length !== b.length) return false;
        for (let i = 0; i < a.length; i++) {
          if (!this.isEqual(a[i], b[i])) return false;
        }
        return true;
      }
      
      const keysA = Object.keys(a);
      const keysB = Object.keys(b);
      
      if (keysA.length !== keysB.length) return false;
      
      for (const key of keysA) {
        if (!keysB.includes(key)) return false;
        if (!this.isEqual(a[key], b[key])) return false;
      }
      
      return true;
    }
    
    return false;
  }
}

export default AuditService;
